import { useState, useEffect } from 'react';
import { X, Building2, Globe, Mail, Phone, Palette, AlertTriangle, Check, Percent, Layout } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Textarea } from '../../components/ui/Textarea.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import {
  HEADER_SLOT_OPTIONS,
  HERO_SLOT_OPTIONS,
  CARD_SLOT_OPTIONS,
  FILTER_SLOT_OPTIONS,
} from './admin-presets.js';

function parseMarkupPercent(setting) {
  if (setting === undefined || setting === null) return '';
  if (typeof setting === 'number') return setting;
  if (typeof setting === 'string') {
    const num = parseFloat(setting);
    return isNaN(num) ? '' : num;
  }
  if (typeof setting === 'object') {
    if (setting.percent !== undefined && setting.percent !== null) return Number(setting.percent);
    if (setting.markup !== undefined && setting.markup !== null) return Number((setting.markup * 100).toFixed(2));
  }
  return '';
}

function getInitialFormData(tenant) {
  if (tenant) {
    const addr = typeof tenant.address === 'object' && tenant.address ? tenant.address : {};
    return {
      name: tenant.name || '',
      slug: tenant.slug || '',
      domain: tenant.domain || '',
      contactEmail: tenant.contactEmail || tenant.contact_email || '',
      phone: tenant.phone || '',
      primaryColor: tenant.primaryColor || tenant.primary_color || '#0056b3',
      secondaryColor: tenant.secondaryColor || tenant.secondary_color || '#5f5e5f',
      accentColor: tenant.accentColor || tenant.accent_color || tenant.settings?.branding?.accentColor || '#ff9900',
      logoUrl: tenant.logoUrl || tenant.logo_url || '',
      street: addr.street || (typeof tenant.address === 'string' ? tenant.address : ''),
      zip: addr.zip || '',
      city: addr.city || '',
      country: addr.country || 'DE',
      imprint: tenant.imprint || '',
      privacyPolicy: tenant.privacyPolicy || tenant.privacy_policy || '',
      isActive: tenant.isActive !== undefined ? Boolean(tenant.isActive) : true,
      pricingMarkupPercent: parseMarkupPercent(tenant.settings?.pricing_markup_percent),
      layout_header: tenant.settings?.layout_header || tenant.layout_header || 'slim',
      layout_hero: tenant.settings?.layout_hero || tenant.layout_hero || 'portal_search',
      product_card_variant: tenant.settings?.product_card_variant || tenant.product_card_variant || 'specs_heavy',
      filter_layout: tenant.settings?.filter_layout || tenant.filter_layout || 'sidebar',
    };
  }
  return {
    name: '',
    slug: '',
    domain: '',
    contactEmail: '',
    phone: '',
    primaryColor: '#0056b3',
    secondaryColor: '#5f5e5f',
    accentColor: '#ff9900',
    logoUrl: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    imprint: '',
    privacyPolicy: '',
    isActive: true,
    pricingMarkupPercent: '',
    layout_header: 'slim',
    layout_hero: 'portal_search',
    product_card_variant: 'specs_heavy',
    filter_layout: 'sidebar',
  };
}

export function TenantModal({
  isOpen,
  onClose,
  tenant = null,
  onSave,
  saving = false,
  error = null,
}) {
  const isEdit = Boolean(tenant && tenant.id);

  const [formData, setFormData] = useState(() => getInitialFormData(tenant));

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (tenant) {
      const addr = typeof tenant.address === 'object' && tenant.address ? tenant.address : {};
      setFormData({
        name: tenant.name || '',
        slug: tenant.slug || '',
        domain: tenant.domain || '',
        contactEmail: tenant.contactEmail || tenant.contact_email || '',
        phone: tenant.phone || '',
        primaryColor: tenant.primaryColor || tenant.primary_color || '#0056b3',
        secondaryColor: tenant.secondaryColor || tenant.secondary_color || '#5f5e5f',
        accentColor: tenant.accentColor || tenant.accent_color || tenant.settings?.branding?.accentColor || '#ff9900',
        logoUrl: tenant.logoUrl || tenant.logo_url || '',
        street: addr.street || (typeof tenant.address === 'string' ? tenant.address : ''),
        zip: addr.zip || '',
        city: addr.city || '',
        country: addr.country || 'DE',
        imprint: tenant.imprint || '',
        privacyPolicy: tenant.privacyPolicy || tenant.privacy_policy || '',
        isActive: tenant.isActive !== undefined ? Boolean(tenant.isActive) : true,
        pricingMarkupPercent: parseMarkupPercent(tenant.settings?.pricing_markup_percent),
        layout_header: tenant.settings?.layout_header || tenant.layout_header || 'slim',
        layout_hero: tenant.settings?.layout_hero || tenant.layout_hero || 'portal_search',
        product_card_variant: tenant.settings?.product_card_variant || tenant.product_card_variant || 'specs_heavy',
        filter_layout: tenant.settings?.filter_layout || tenant.filter_layout || 'sidebar',
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        domain: '',
        contactEmail: '',
        phone: '',
        primaryColor: '#0056b3',
        secondaryColor: '#5f5e5f',
        accentColor: '#ff9900',
        logoUrl: '',
        street: '',
        zip: '',
        city: '',
        country: 'DE',
        imprint: '',
        privacyPolicy: '',
        isActive: true,
        pricingMarkupPercent: '',
        layout_header: 'slim',
        layout_hero: 'portal_search',
        product_card_variant: 'specs_heavy',
        filter_layout: 'sidebar',
      });
    }
    setValidationErrors({});
  }, [tenant, isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Unternehmensname ist erforderlich';
    if (!formData.slug.trim()) errs.slug = 'Mandanten-Slug ist erforderlich';
    if (!formData.domain.trim()) errs.domain = 'Domain ist erforderlich';
    if (!formData.contactEmail.trim()) {
      errs.contactEmail = 'Kontakt-E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail.trim())) {
      errs.contactEmail = 'Ungültige E-Mail-Adresse';
    }
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      domain: formData.domain.trim(),
      contactEmail: formData.contactEmail.trim(),
      phone: formData.phone.trim() || null,
      primaryColor: formData.primaryColor,
      secondaryColor: formData.secondaryColor,
      logoUrl: formData.logoUrl.trim() || null,
      address: {
        company: formData.name.trim(),
        street: formData.street.trim(),
        zip: formData.zip.trim(),
        city: formData.city.trim(),
        country: formData.country.trim() || 'DE',
      },
      imprint: formData.imprint.trim() || null,
      privacyPolicy: formData.privacyPolicy.trim() || null,
      isActive: formData.isActive ? 1 : 0,
      settings: {
        ...(tenant?.settings || {}),
        theme_radius: '0.375rem',
        layout_header: formData.layout_header || 'slim',
        layout_hero: formData.layout_hero || 'portal_search',
        product_card_variant: formData.product_card_variant || 'specs_heavy',
        filter_layout: formData.filter_layout || 'sidebar',
        ...(formData.pricingMarkupPercent !== '' && !isNaN(parseFloat(formData.pricingMarkupPercent))
          ? {
              pricing_markup_percent: {
                markup: Number((parseFloat(formData.pricingMarkupPercent) / 100).toFixed(4)),
                percent: parseFloat(formData.pricingMarkupPercent),
              },
            }
          : (tenant?.settings?.pricing_markup_percent !== undefined ? { pricing_markup_percent: null } : {})),
        branding: {
          ...(tenant?.settings?.branding || {}),
          ...(tenant?.settings?.branding?.slogan !== undefined ? { slogan: tenant.settings.branding.slogan } : {}),
          accentColor: formData.accentColor || tenant?.settings?.branding?.accentColor || '#ff9900',
        },
      },
    };

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-xs">
      <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl border border-[var(--border)] max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[var(--primary)]" />
            <h2 className="font-display text-lg font-bold text-[var(--foreground)]">
              {isEdit ? `Mandant bearbeiten: ${tenant?.name}` : 'Neuen Partner-Mandanten anlegen'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body / Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Fehler beim Speichern</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basisdaten */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
              1. Stammdaten & Domain
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">
                  Unternehmensname *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      name: val,
                      // Auto-generate slug if creating new
                      slug: !isEdit && !prev.slugModified ? val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : prev.slug,
                    }));
                  }}
                  placeholder="z.B. Alpha Sicherheitstechnik GmbH"
                />
                {validationErrors.name && (
                  <p className="text-xs text-rose-600">{validationErrors.name}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">
                  Mandanten-Slug (Eindeutig) *
                </label>
                <Input
                  value={formData.slug}
                  disabled={isEdit} // Slug usually immutable after creation
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      slugModified: true,
                    }))
                  }
                  placeholder="z.B. alpha"
                  className={isEdit ? 'bg-slate-50 font-mono text-xs' : 'font-mono text-xs'}
                />
                {validationErrors.slug && (
                  <p className="text-xs text-rose-600">{validationErrors.slug}</p>
                )}
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-semibold text-[var(--foreground)]">
                  Shop-Domain / Subdomain (Hostname) *
                </label>
                <div className="relative">
                  <Globe className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    value={formData.domain}
                    onChange={(e) => setFormData((prev) => ({ ...prev, domain: e.target.value.toLowerCase() }))}
                    placeholder="shop.alpha-sicherheit.de oder localhost:8789"
                    className="pl-9 font-mono text-xs"
                  />
                </div>
                {validationErrors.domain && (
                  <p className="text-xs text-rose-600">{validationErrors.domain}</p>
                )}
              </div>
            </div>
          </div>

          {/* Kontaktdaten */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
              2. Kontakt & Anschrift
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">
                  Kontakt-E-Mail *
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData((prev) => ({ ...prev, contactEmail: e.target.value }))}
                    placeholder="kontakt@alpha.de"
                    className="pl-9"
                  />
                </div>
                {validationErrors.contactEmail && (
                  <p className="text-xs text-rose-600">{validationErrors.contactEmail}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">
                  Telefonnummer
                </label>
                <div className="relative">
                  <Phone className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="+49 30 11223344"
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-semibold text-[var(--foreground)]">Straße & Hausnummer</label>
                <Input
                  value={formData.street}
                  onChange={(e) => setFormData((prev) => ({ ...prev, street: e.target.value }))}
                  placeholder="Industriestraße 10"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">PLZ</label>
                <Input
                  value={formData.zip}
                  onChange={(e) => setFormData((prev) => ({ ...prev, zip: e.target.value }))}
                  placeholder="20095"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">Stadt</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="Hamburg"
                />
              </div>
            </div>
          </div>

          {/* Branding & Farben */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
              3. Branding & Farbschema
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">Primärfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    className="h-9 w-9 rounded-md border border-[var(--border)] p-0.5 cursor-pointer"
                  />
                  <Input
                    value={formData.primaryColor}
                    onChange={(e) => setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))}
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[var(--foreground)]">Sekundärfarbe</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                    className="h-9 w-9 rounded-md border border-[var(--border)] p-0.5 cursor-pointer"
                  />
                  <Input
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData((prev) => ({ ...prev, secondaryColor: e.target.value }))}
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-semibold text-[var(--foreground)]">Logo URL</label>
                <Input
                  value={formData.logoUrl}
                  onChange={(e) => setFormData((prev) => ({ ...prev, logoUrl: e.target.value }))}
                  placeholder="/assets/logos/partner-logo.svg oder https://..."
                />
              </div>
            </div>
          </div>

          {/* B2B-Marge & Astryx Slot-Layout */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
              4. B2B-Marge & Astryx Slot-Layout
            </h3>
            
            <div className="space-y-4">
              {/* Margenaufschlag */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-[var(--foreground)]">
                    Standard-Margenaufschlag (%)
                  </label>
                  <span className="text-xs font-mono font-bold text-[var(--primary)]">
                    +{Number(formData.pricingMarkupPercent || 0).toFixed(1)} %
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="200"
                      value={formData.pricingMarkupPercent}
                      onChange={(e) => setFormData((prev) => ({ ...prev, pricingMarkupPercent: e.target.value }))}
                      className="pr-8 text-xs font-mono"
                      placeholder="15.0"
                    />
                    <span className="pointer-events-none absolute right-3 top-2 text-xs font-semibold text-slate-400">
                      %
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 pt-1">
                  {[0, 10, 15, 20, 25, 30].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, pricingMarkupPercent: pct }))}
                      className={`rounded-md px-2 py-0.5 text-[10px] font-semibold transition-all ${
                        Number(formData.pricingMarkupPercent) === pct
                          ? 'bg-[var(--primary)] text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      +{pct}%
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500">
                  Globaler Aufschlag auf ASL-Einkaufspreise für alle Katalogartikel ohne individuellen Festpreis.
                </p>
              </div>

              {/* Slot Layout Selectors */}
              <div className="grid gap-3 sm:grid-cols-2 pt-2 border-t border-[var(--border)]">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Header-Layout</label>
                  <select
                    value={formData.layout_header}
                    onChange={(e) => setFormData((prev) => ({ ...prev, layout_header: e.target.value }))}
                    className="w-full rounded-md border border-[var(--border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-[var(--primary)]"
                  >
                    {HEADER_SLOT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Hero-Banner</label>
                  <select
                    value={formData.layout_hero}
                    onChange={(e) => setFormData((prev) => ({ ...prev, layout_hero: e.target.value }))}
                    className="w-full rounded-md border border-[var(--border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-[var(--primary)]"
                  >
                    {HERO_SLOT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Produktkarten-Stil</label>
                  <select
                    value={formData.product_card_variant}
                    onChange={(e) => setFormData((prev) => ({ ...prev, product_card_variant: e.target.value }))}
                    className="w-full rounded-md border border-[var(--border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-[var(--primary)]"
                  >
                    {CARD_SLOT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Filter-Layout</label>
                  <select
                    value={formData.filter_layout}
                    onChange={(e) => setFormData((prev) => ({ ...prev, filter_layout: e.target.value }))}
                    className="w-full rounded-md border border-[var(--border)] bg-white px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-[var(--primary)]"
                  >
                    {FILTER_SLOT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.value})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Rechtstexte */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--secondary)]">
                5. Rechtliche Angaben (HTML)
              </h3>
              {(!formData.imprint?.trim() || !formData.privacyPolicy?.trim()) && (
                <span data-testid="legal-not-provided-badge" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 font-medium">
                  Nicht hinterlegt (optional)
                </span>
              )}
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Impressum</label>
                  {!formData.imprint?.trim() && (
                    <span data-testid="imprint-not-provided" className="text-[10px] text-amber-600 font-medium">Nicht hinterlegt</span>
                  )}
                </div>
                <Textarea
                  value={formData.imprint}
                  onChange={(e) => setFormData((prev) => ({ ...prev, imprint: e.target.value }))}
                  placeholder="Kein Impressum hinterlegt. Optionales HTML-Impressum hier einfügen..."
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-[var(--foreground)]">Datenschutzerklärung</label>
                  {!formData.privacyPolicy?.trim() && (
                    <span data-testid="privacy-not-provided" className="text-[10px] text-amber-600 font-medium">Nicht hinterlegt</span>
                  )}
                </div>
                <Textarea
                  value={formData.privacyPolicy}
                  onChange={(e) => setFormData((prev) => ({ ...prev, privacyPolicy: e.target.value }))}
                  placeholder="Keine Datenschutzerklärung hinterlegt. Optionales HTML hier einfügen..."
                  rows={3}
                  className="font-mono text-xs"
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 pt-2 border-t border-[var(--border)]">
            <input
              type="checkbox"
              id="tenant-active-checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded-sm border-slate-300 text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <label htmlFor="tenant-active-checkbox" className="text-sm font-medium text-[var(--foreground)] cursor-pointer">
              Mandant ist aktiv und über Domain erreichbar
            </label>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[var(--border)] px-6 py-4 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Speichere...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                {isEdit ? 'Änderungen speichern' : 'Mandant anlegen'}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
